
import axios from "axios";
import { useNavigate } from "react-router-dom";  // Thêm import

const API_URL = process.env.REACT_APP_API_URL;

// ================= Helper: Lấy token =================
const getToken = () => localStorage.getItem("token");  // Lấy access token từ localStorage
const getRefreshToken = () => localStorage.getItem("refresh_token");  // Lấy refresh token từ localStorage

// ================= Kiểm tra headers =================
const getAuthHeaders = () => {
  const token = getToken();
  if (!token) throw new Error("Chưa đăng nhập");
  return { Authorization: `Bearer ${token}` };
};

// ================= LÀM MỚI TOKEN =================
export const refreshToken = async () => {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error("Không có refresh token");
  }

  try {
    const response = await axios.post(`${API_URL}/users/refresh`, { refreshToken: refreshTokenValue });
    const { token } = response.data;

    localStorage.setItem("token", token);
    return token;
  } catch (error) {
    console.error("❌ Lỗi làm mới token:", error);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("refresh_token");
    window.location.href = "/login";
    throw new Error("Refresh token hết hạn, vui lòng đăng nhập lại");
  }
};

// ================= ĐĂNG KÝ =================
export const register = async (userData, navigate) => {
  try {
    const response = await axios.post(`${API_URL}/users/register`, userData);
    return response.data;
  } catch (error) {
    console.error("❌ Error during registration:", error);
    if (navigate) navigate("/login");
    throw error.response?.data || "Đã có lỗi xảy ra khi đăng ký.";
  }
};

// ================= XÁC MINH EMAIL =================
export const verifyEmail = async (email, verificationCode) => {
  try {
    const response = await axios.post(`${API_URL}/users/verify-email`, {
      email,
      verificationCode,
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error during email verification:", error);
    throw error.response?.data || "Đã có lỗi xảy ra khi xác minh email.";
  }
};

// ================= GỬI LẠI MÃ XÁC MINH =================
export const resendVerificationCode = async (email) => {
  try {
    const response = await axios.post(`${API_URL}/users/resend-verification`, { email });
    return response.data;
  } catch (error) {
    console.error("❌ Error during resend verification:", error);
    throw error.response?.data || "Đã có lỗi xảy ra khi gửi lại mã xác minh.";
  }
};

// ================= ĐĂNG NHẬP =================
export const login = async (credentials, navigate) => {
  try {
    const response = await axios.post(`${API_URL}/users/login`, credentials);
    const { token, user, refreshToken } = response.data;

    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("refresh_token", refreshToken);

    return { token, user, refreshToken };
  } catch (error) {
    console.error("❌ Error during login:", error);
    if (navigate) navigate("/login");
    throw error.response?.data || "Đã có lỗi xảy ra khi đăng nhập.";
  }
};

// ================= LẤY USER HIỆN TẠI =================
export const getMe = async (navigate) => {
  try {
    const response = await axios.get(`${API_URL}/users/me`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching current user:", error);
    if (navigate) navigate("/login");
    throw error.response?.data || new Error("Chưa đăng nhập");
  }
};

// ================= LẤY USER TỪ TOKEN LOCAL =================
export const getUserFromToken = () => {
  try {
    const token = getToken();
    if (!token) return null;

    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (error) {
    console.error("❌ Error decoding token:", error);
    return null;
  }
};

// ================= ĐĂNG XUẤT =================
export const logout = async (navigate) => {
  try {
    await axios.post(`${API_URL}/users/logout`);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("refresh_token");
    return { message: "Đăng xuất thành công" };
  } catch (error) {
    console.error("❌ Error during logout:", error);
    if (navigate) navigate("/login");
    throw error.response?.data || "Đã có lỗi xảy ra khi đăng xuất.";
  }
};

// ================= KIỂM TRA ĐĂNG NHẬP =================
export const isLoggedIn = () => !!getToken();

// ================= UPDATE USER =================
export const updateUser = async (data, navigate) => {
  try {
    const response = await axios.put(`${API_URL}/users/me`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error updating user:", error);
    if (navigate) navigate("/login");
    throw error.response?.data || error.message || "Đã có lỗi xảy ra khi cập nhật thông tin.";
  }
};

// ================= LẤY DANH SÁCH NHÂN SỰ =================
export const getStaff = async (navigate) => {
  try {
    const res = await axios.get(`${API_URL}/users/staff`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Error fetching staff:", err);
    if (navigate) navigate("/login");
    throw err.response?.data || "Không thể lấy danh sách nhân sự";
  }
};

// ================= LẤY DOANH THU + HOA HỒNG NHÂN SỰ =================
export const getStaffRevenue = async (navigate) => {
  try {
    const res = await axios.get(`${API_URL}/users/staff/revenue`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Error fetching staff revenue:", err);
    if (navigate) navigate("/login");
    throw err.response?.data || "Không thể lấy doanh thu nhân sự";
  }
};

// ================= UPDATE STAFF =================
export const updateStaff = async (id, data, navigate) => {
  try {
    const res = await axios.put(`${API_URL}/users/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Error updating staff:", err);
    if (navigate) navigate("/login");
    throw err.response?.data || "Không thể cập nhật nhân sự";
  }
};

// ================= DELETE STAFF =================
export const deleteStaff = async (id, navigate) => {
  try {
    const res = await axios.delete(`${API_URL}/users/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("❌ Error deleting staff:", err);
    if (navigate) navigate("/login");
    throw err.response?.data || "Không thể xóa nhân sự";
  }
};

// ================= REGISTER NHÂN SỰ =================
export const registerManagement = async (userData) => {
  try {
    const response = await axios.post(
      `${API_URL}/users/register-management`,
      userData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error during management registration:", error);
    throw error.response?.data || "Đã có lỗi xảy ra khi tạo tài khoản nhân sự.";
  }
};

// ================= KIỂM TRA TRẠNG THÁI XÁC MINH EMAIL =================
export const checkEmailVerification = async (email) => {
  try {
    const response = await axios.get(`${API_URL}/users/check-verification-status`, {
      params: { email },
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error checking email verification:", error);
    throw error.response?.data || "Không thể kiểm tra trạng thái xác minh email.";
  }
};

// ================= 📤 UPLOAD QR THANH TOÁN (CHO BOSS) =================
export const uploadPaymentQR = async (formData) => {
  try {
    const res = await axios.post(`${API_URL}/users/upload-qr`, formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data; // { message, paymentQR }
  } catch (error) {
    console.error("❌ Error uploading payment QR:", error);
    throw error.response?.data || "Không thể upload mã QR thanh toán.";
  }
};

// ================= SETUP AXIOS INTERCEPTOR =================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const setupAxiosInterceptors = () => {
  // ✅ Request Interceptor: Thêm Authorization header vào tất cả requests
  axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ✅ Response Interceptor: Tự động làm mới token nếu hết hạn (401)
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (originalRequest?.url?.includes("/users/login")) {
        return Promise.reject(error);
      }

      // Nếu lỗi là 401 (token hết hạn) và chưa retry
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // Nếu đang refresh, thêm request vào queue
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Gọi refresh token
          const newToken = await refreshToken();
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (err) {
          processQueue(err, null);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("refresh_token");
          window.location.href = "/login";
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
